import { getAccessToken } from '../auth/get-access-token.js';
import { clientEnv } from '../types/env.js';
import { handleResponse } from './api-error.js';

type RequestInitJson = Omit<RequestInit, 'body' | 'headers'> & {
  body?: unknown;
  headers?: Record<string, string>;
};

// On a 401, retry once with a forced refresh. Defends against clock skew,
// JWKS-cache misses after WorkOS key rotation, and the residual exp-window
// race. A second 401 means the session is genuinely no longer valid; let
// the ApiError surface so AppShell renders the session-invalidated panel.
export async function apiFetch<T>(path: string, init: RequestInitJson = {}): Promise<T> {
  const response = await sendRequest(path, init, { forceRefresh: false });
  if (response.status !== 401) {
    return handleResponse<T>(response);
  }
  const retried = await sendRequest(path, init, { forceRefresh: true });
  return handleResponse<T>(retried);
}

async function sendRequest(
  path: string,
  init: RequestInitJson,
  tokenOptions: { forceRefresh: boolean },
): Promise<Response> {
  // Pull body + headers off so `restInit` carries only RequestInit-compatible
  // fields when spread into fetch().
  const { body: rawBody, headers: callerHeaders, ...restInit } = init;
  const token = await getAccessToken({ forceRefresh: tokenOptions.forceRefresh });
  const headers: Record<string, string> = {
    accept: 'application/json',
    ...(token ? { authorization: `Bearer ${token}` } : {}),
    ...callerHeaders,
  };
  let body: BodyInit | undefined;
  if (rawBody !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(rawBody);
  }
  // exactOptionalPropertyTypes refuses an explicit `body: undefined` on the
  // RequestInit, so include the key only when there's a body to send.
  return fetch(`${clientEnv.VITE_API_URL}${path}`, {
    ...restInit,
    headers,
    ...(body !== undefined ? { body } : {}),
  });
}
